import { ClaimReservationWithTimeslot } from '@Generated/back-office-client/src'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'

export const useTimeslotAvailability = (
  reservation: ClaimReservationWithTimeslot,
  claimCenterId: string,
  numberOfTickets: number,
) => {
  const [selectedDate, setSelectedDate] = useState<string>(reservation.timeslot.display.localDate)
  const [selectedTimeslotId, setSelectedTimeslotId] = useState<string>(reservation.timeslot.id)

  const { data: availability, isLoading: isAvailabilityLoading } = useQuery({
    queryKey: [
      `edit-availability-${claimCenterId}-${selectedDate}-${numberOfTickets}-${reservation.id}`,
    ],
    queryFn: async () => {
      return await ClaimReservationsServiceAPI.getClaimCentersAvailability(
        selectedDate,
        claimCenterId,
        numberOfTickets,
        reservation.id,
      )
    },
  })

  const availableTimeslots = useMemo(
    () => availability?.flatMap((a) => a.timeslots) ?? [],
    [availability],
  )

  useEffect(() => {
    if (!availability) {
      return
    }

    const currentStillAvailable = availableTimeslots.find((t) => t.id === selectedTimeslotId)
    if (currentStillAvailable) {
      return
    }

    const currentDisplayTime = reservation.timeslot.display.localTime
    const matchByTime = availableTimeslots.find((t) => t.display.localTime === currentDisplayTime)
    if (matchByTime) {
      setSelectedTimeslotId(matchByTime.id)
      return
    }

    if (availableTimeslots.length > 0) {
      setSelectedTimeslotId(availableTimeslots[0].id)
    }
  }, [availability, availableTimeslots, reservation.timeslot.display.localTime, selectedTimeslotId])

  const hasTimeslotChanged = selectedTimeslotId !== reservation.timeslot.id

  return {
    selectedDate,
    setSelectedDate,
    selectedTimeslotId,
    setSelectedTimeslotId,
    availableTimeslots,
    isAvailabilityLoading,
    hasTimeslotChanged,
  }
}
